import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useDynamicReducer } from '@micro-frontend-react/employee-experience/lib/useDynamicReducer';
import { Context } from '@micro-frontend-react/employee-experience/lib/Context';
import type { IEmployeeExperienceContext } from '@micro-frontend-react/employee-experience/lib/IEmployeeExperienceContext';

import { pbtCrmCasesReducerName, pbtCrmCasesReducer } from '../../../Redux/PBT/CRMCases/reducers';
import { requestCrmCasesSagas } from '../../../Services/PBTCRMService';
import { requestCrmCases } from '../../../Redux/PBT/CRMCases/actions';
import { createCaseReset } from '../../../Redux/PBT/CreateCase/actions';
import type { ICRMCasesState } from '../../../Redux/PBT/CRMCases/types';
import type { ICRMCase } from '../../../Interfaces/ICRMCase';
import { PBTFlag } from '../../../Interfaces/IPBTCase';
import { CRM_SEARCH_DEBOUNCE_MS, getCrmSearchRequest, getDebouncedSearchTerm } from './crmSearch';
import { canReuseLoadedCrmCasesForBulk, getCrmCasesFromPayload } from './crmBulk';
import { PBTApiEndpoints } from '../../../Services/PBTConstants';

const defaultCrmState: ICRMCasesState = {
  cases: [],
  pageNumber: 1,
  pageSize: 15,
  totalPages: 0,
  totalCount: 0,
  gerWitCount: 0,
  loaCount: 0,
  duplicateCount: 0,
  isLoading: false,
  hasError: false,
  errorMessage: null,
  searchString: '',
};

export function useCRMCases() {
  useDynamicReducer(pbtCrmCasesReducerName, pbtCrmCasesReducer, [requestCrmCasesSagas]);
  const { authClient } = React.useContext(Context as React.Context<IEmployeeExperienceContext>);

  const crmState = useSelector(
    (state: { dynamic?: Record<string, unknown> }) =>
      (state?.dynamic?.[pbtCrmCasesReducerName] as ICRMCasesState) ?? defaultCrmState
  );
  const dispatch = useDispatch();
  const history = useHistory();

  const [searchText, setSearchText] = React.useState('');
  const [selectedCase, setSelectedCase] = React.useState<ICRMCase | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = React.useState(false);
  const [isBulkOpen, setIsBulkOpen] = React.useState(false);
  const [bulkCases, setBulkCases] = React.useState<ICRMCase[]>([]);
  const [isBulkLoading, setIsBulkLoading] = React.useState(false);
  const [bulkErrorMessage, setBulkErrorMessage] = React.useState<string | null>(null);
  const [successMessage, setSuccessMessage] = React.useState<string | null>(null);

  React.useEffect(() => {
    dispatch(requestCrmCases());
  }, [dispatch]);

  React.useEffect(() => {
    const searchString = getDebouncedSearchTerm(searchText, crmState.searchString);
    if (searchString === null) {
      return;
    }

    const timer = window.setTimeout(() => {
      const request = getCrmSearchRequest(searchString, crmState.pageSize);
      dispatch(requestCrmCases(request.pageNumber, request.pageSize, request.searchString));
    }, CRM_SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [dispatch, crmState.pageSize, crmState.searchString, searchText]);

  const filteredCases = crmState.cases;

  const handleSearch = React.useCallback(
    (value?: string) => {
      const request = getCrmSearchRequest(value ?? searchText, crmState.pageSize);
      setSearchText(request.searchString);
      dispatch(requestCrmCases(request.pageNumber, request.pageSize, request.searchString));
    },
    [crmState.pageSize, dispatch, searchText]
  );

  const handleEnterDetails = React.useCallback((c: ICRMCase) => {
    setSelectedCase(c);
    setIsDetailsOpen(true);
  }, []);

  const handleDetailsSuccess = React.useCallback(() => {
    setIsDetailsOpen(false);
    setSelectedCase(null);
    dispatch(createCaseReset());
    history.push('/PBT/Dashboard', {
      successMessage: 'Termination details submitted — PBT workflow initiated.',
    });
  }, [history, dispatch]);

  const handleBulkSuccess = React.useCallback(
    (successCount: number, failureCount: number) => {
      setIsBulkOpen(false);
      dispatch(createCaseReset());
      history.push('/PBT/Dashboard', {
        successMessage: `Bulk creation complete: ${successCount} case(s) created${
          failureCount > 0 ? `, ${failureCount} failed` : ''
        }.`,
      });
    },
    [history, dispatch]
  );

  const getCaseFlags = React.useCallback((c: ICRMCase): PBTFlag[] => {
    const flags: PBTFlag[] = [];
    if (c.isGerWit) flags.push(PBTFlag.GERWIT);
    if (c.isLoa) flags.push(PBTFlag.LOA);
    return flags;
  }, []);

  const handleRefresh = React.useCallback(() => {
    dispatch(requestCrmCases(crmState.pageNumber, crmState.pageSize, crmState.searchString));
  }, [dispatch, crmState.pageNumber, crmState.pageSize, crmState.searchString]);

  const handlePageChange = React.useCallback(
    (pageNumber: number) => {
      dispatch(requestCrmCases(pageNumber, crmState.pageSize, crmState.searchString));
    },
    [dispatch, crmState.pageSize, crmState.searchString]
  );

  const handleOpenBulk = React.useCallback(async () => {
    setIsBulkLoading(true);
    setBulkErrorMessage(null);
    try {
      if (
        canReuseLoadedCrmCasesForBulk(
          crmState.isLoading,
          crmState.hasError,
          crmState.totalCount,
          crmState.cases.length,
          crmState.searchString
        )
      ) {
        setBulkCases(crmState.cases);
      } else {
        const token = await authClient.acquireToken(__SCOPES__);
        const firstResponse = await fetch(PBTApiEndpoints.CRMIncoming(1, 100), {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        });
        if (!firstResponse.ok) throw new Error(`API returned ${firstResponse.status}`);
        const firstBody = await firstResponse.json();
        const firstData = firstBody?.data ?? firstBody?.Data;

        const pages = Number(firstData?.totalPages ?? firstData?.TotalPages ?? 1);
        const remaining = await Promise.all(
          Array.from({ length: Math.max(0, pages - 1) }, (_, index) => index + 2).map(async (page) => {
            const response = await fetch(PBTApiEndpoints.CRMIncoming(page, 100), {
              headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            });
            if (!response.ok) throw new Error(`API returned ${response.status}`);
            const body = await response.json();
            const data = body?.data ?? body?.Data;
            return getCrmCasesFromPayload(data);
          })
        );
        setBulkCases([...getCrmCasesFromPayload(firstData), ...remaining.flat()]);
      }
      setIsBulkOpen(true);
    } catch (error) {
      console.error('Failed to load CRM cases for bulk entry:', error);
      setBulkErrorMessage('Unable to load all authorized CRM cases for bulk entry. Please try again.');
    } finally {
      setIsBulkLoading(false);
    }
  }, [authClient, crmState.cases, crmState.hasError, crmState.isLoading, crmState.searchString, crmState.totalCount]);

  const handleDismissDetails = React.useCallback(() => {
    setIsDetailsOpen(false);
    setSelectedCase(null);
  }, []);

  const handleDismissBulk = React.useCallback(() => {
    setIsBulkOpen(false);
  }, []);

  return {
    crmState,
    filteredCases,
    searchText,
    setSearchText,
    selectedCase,
    isDetailsOpen,
    isBulkOpen,
    bulkCases,
    isBulkLoading,
    bulkErrorMessage,
    setBulkErrorMessage,
    successMessage,
    setSuccessMessage,
    handleEnterDetails,
    handleDetailsSuccess,
    handleBulkSuccess,
    getCaseFlags,
    handleSearch,
    handleRefresh,
    handlePageChange,
    handleOpenBulk,
    handleDismissDetails,
    handleDismissBulk,
  };
}
